import React, {memo, useCallback, useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import URLSearchParams from 'url-search-params'
import {Route, Switch, useHistory, useLocation, useRouteMatch} from "react-router-dom";
import {ConfigProvider, message} from 'antd';
import {IntlProvider} from "react-intl";
import moment from "moment";
import AppLocale from "../../lngProvider";
import MainApp from "./MainApp";
import {
    fetchRecords,
    getUser,
    setApplicationAuthorization,
    setExpiresSignature, setIdleId,
    setInitUrl, setWarningId, userSignOut
} from "../../appRedux/actions";


import {
    LAYOUT_TYPE_BOXED,
    LAYOUT_TYPE_FRAMED,
    LAYOUT_TYPE_FULL,
    NAV_STYLE_ABOVE_HEADER,
    NAV_STYLE_BELOW_HEADER,
    NAV_STYLE_DARK_HORIZONTAL,
    NAV_STYLE_DEFAULT_HORIZONTAL,
    NAV_STYLE_INSIDE_HEADER_HORIZONTAL
} from "../../constants/ThemeSetting";

import CircularProgress from "../../components/CircularProgress";
import {GET_USER_AUTHORIZATION, LOGIN_PAGE} from "../../constants/ServerUrl";
import {USER_TOKEN_SET} from "../../constants/ActionTypes";
import {getActiveCompany} from "../../appRedux/actions";

const DEFAULT_IDLE_TIME = 1800000; // 30 mins
const WARNING_OFFSET_TIME = 300000; // 5 mins
const ACTIVITY_DEBOUNCE_TIME = 1000; // 1 sec

const parseTrackingMoment = (value, baseDate = moment()) => {
    if (!value) return null;

    // ISO datetime from admin (e.g. "2025-01-14T05:00:00.382Z")
    // → use only the clock time on today's date
    if (String(value).includes("T")) {
        const parsed = moment(value);
        if (!parsed.isValid()) return null;

        return baseDate.clone()
            .hour(parsed.hour())
            .minute(parsed.minute())
            .second(parsed.second())
            .millisecond(0);
    }

    // Plain time strings (e.g. "05:00:00" or "17:00")
    const asTime = moment(value, ["HH:mm:ss", "HH:mm"], true);
    if (asTime.isValid()) {
        return baseDate.clone()
            .hour(asTime.hour())
            .minute(asTime.minute())
            .second(asTime.second())
            .millisecond(0);
    }

    return null;
};

const getTrackingWindow = (sessionTracking) => {
    if (!sessionTracking || sessionTracking.status === false) return null;

    const now = moment();
    let start = parseTrackingMoment(sessionTracking.start_time, now);
    let end = parseTrackingMoment(sessionTracking.end_time, now);

    if (!start || !end) return null;

    // Overnight window support (e.g. 20:00 -> 05:00)
    if (end.isSameOrBefore(start)) {
        end.add(1, "day");
        if (now.isBefore(start)) {
            start.subtract(1, "day");
            end.subtract(1, "day");
        }
    }

    return { start, end };
};

const getSessionRemainingTime = (sessionTracking) => {
    const trackingWindow = getTrackingWindow(sessionTracking);
    if (!trackingWindow) return null;

    const now = moment();
    if (now.isSameOrAfter(trackingWindow.start) && now.isBefore(trackingWindow.end)) {
        return trackingWindow.end.diff(now);
    }

    return null;
};

const getIdleDuration = (authUser) => {
    if (!authUser?.enable_tracking) {
        return DEFAULT_IDLE_TIME;
    }

    const activeRemaining = getSessionRemainingTime(authUser?.active_session_tracking);
    const passiveRemaining = getSessionRemainingTime(authUser?.passive_session_tracking);

    if (activeRemaining !== null && passiveRemaining !== null) {
        return Math.max(activeRemaining, passiveRemaining);
    }

    if (activeRemaining !== null) return activeRemaining;
    if (passiveRemaining !== null) return passiveRemaining;

    // Outside both active/passive windows
    return DEFAULT_IDLE_TIME;
};


const RestrictedRoute = ({component: Component, location, token, ...rest}) =>
    <Route
        {...rest}
        render={props =>
            token
                ? <Component {...props} />
                : <CircularProgress/>}
    />;


const App = () => {

    const dispatch = useDispatch();
    const {locale, navStyle, layoutType} = useSelector(({settings}) => settings);
    const {token, initURL, authUser} = useSelector(({auth}) => auth);
    const {loading, error} = useSelector(({common}) => common);
    const location = useLocation();
    const history = useHistory();
    const match = useRouteMatch();
    const activityTimeoutId = useRef(null);
    const idleTimerId = useRef(null);
    const warningTimerId = useRef(null);
    const authUserRef = useRef(authUser);
    authUserRef.current = authUser;

    const resetTimer = useCallback(() => {

        clearTimeout(idleTimerId.current);
        idleTimerId.current = setTimeout(() => {
          //  dispatch(userSignOut());

        }, 1800000);
        // dispatch(setIdleId(idle));

    }, []);

    const resetWarning = useCallback(() => {

        clearTimeout(warningTimerId.current);
        warningTimerId.current = setTimeout(() => {
            message.warn("In-Activity Detect! You will be signed out in 5 minutes");
        }, 1500000);
        // dispatch(setWarningId(warnMe));

    }, []);


    useEffect(() => {
        if (initURL === '') {
            console.log("set init url");
            console.log(location.pathname);
            dispatch(setInitUrl(location.pathname));
        }
        const params = new URLSearchParams(location.search);

        if (params.has("signature") && params.has("expires")) {
            dispatch(setExpiresSignature({signature: params.get('signature'), expires: params.get('expires')}));
        }
        // if (params.has("referer")) {
        //     let token = JSON.stringify(params.get('referer'));
        //     localStorage.setItem("token", token);
        //     dispatch({type: USER_TOKEN_SET, payload: token});
        //     const { pathname } = location;
        //     if (window.location.search) {
        //         history.replace(pathname);
        //     }
        // }
        if (params.has("referer")) {
            let token = JSON.stringify(params.get('referer'));
            localStorage.setItem("token", token);
            dispatch({type: USER_TOKEN_SET, payload: token});
            const { pathname } = location;
            if (window.location.search) {
                window.open(pathname, '_blank');
                window.location.href = pathname;
            }
            
        } else {
            let token = localStorage.getItem("token");
            if (!token) {
                let redirectUrl;
                const currentPath = window.location.href;
                if (currentPath != LOGIN_PAGE) {
                    redirectUrl = `${LOGIN_PAGE}?redirect_url=${encodeURIComponent(currentPath)}`;
                } else {
                    redirectUrl = LOGIN_PAGE
                }
                window.location.href = redirectUrl;
            }
            
        }
        resetTimer();
        resetWarning();

        //
        // if (params.has("theme")) {
        //     dispatch(setThemeType(params.get('theme')));
        // }
        // if (params.has("nav-style")) {
        //     dispatch(onNavStyleChange(params.get('nav-style')));
        // }
        // if (params.has("layout-type")) {
        //     dispatch(onLayoutTypeChange(params.get('layout-type')));
        // }
        // setLayoutType(layoutType);
        // setNavStyle(navStyle);
    }, [dispatch, initURL, layoutType, location.pathname, location.search, navStyle, resetTimer, resetWarning]);

    useEffect(() => {
        const handleActivity = () => {
            if (activityTimeoutId.current) {
                clearTimeout(activityTimeoutId.current);
            }

            activityTimeoutId.current = setTimeout(() => {
                resetTimer();
                resetWarning();
            }, ACTIVITY_DEBOUNCE_TIME);
        };

        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'click'];
        activityEvents.forEach((eventName) => {
            window.addEventListener(eventName, handleActivity, { passive: true });
        });

        return () => {
            activityEvents.forEach((eventName) => {
                window.removeEventListener(eventName, handleActivity);
            });

            if (activityTimeoutId.current) {
                clearTimeout(activityTimeoutId.current);
            }
        };
    }, [resetTimer, resetWarning]);

    useEffect(() => {
        if (authUser) {
            resetTimer();
            resetWarning();
        }
    }, [authUser, resetTimer, resetWarning]);

    useEffect(() => {
        return () => {
            clearTimeout(idleTimerId.current);
            clearTimeout(warningTimerId.current);
        };
    }, []);


    const setLayoutType = (layoutType) => {
        if (layoutType === LAYOUT_TYPE_FULL) {
            document.body.classList.remove('boxed-layout');
            document.body.classList.remove('framed-layout');
            document.body.classList.add('full-layout');
        } else if (layoutType === LAYOUT_TYPE_BOXED) {
            document.body.classList.remove('full-layout');
            document.body.classList.remove('framed-layout');
            document.body.classList.add('boxed-layout');
        } else if (layoutType === LAYOUT_TYPE_FRAMED) {
            document.body.classList.remove('boxed-layout');
            document.body.classList.remove('full-layout');
            document.body.classList.add('framed-layout');
        }
    };

    const setNavStyle = (navStyle) => {
        if (navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
            navStyle === NAV_STYLE_DARK_HORIZONTAL ||
            navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
            navStyle === NAV_STYLE_ABOVE_HEADER ||
            navStyle === NAV_STYLE_BELOW_HEADER) {
            document.body.classList.add('full-scroll');
            document.body.classList.add('horizontal-layout');
        } else {
            document.body.classList.remove('full-scroll');
            document.body.classList.remove('horizontal-layout');
        }
    };
    const getAuthorization = () => {
        fetchRecords(GET_USER_AUTHORIZATION).then((res) => {
            dispatch(setApplicationAuthorization(res.authorizations));

        }).catch(err => {
            console.log(err);
        });
    }
    useEffect(() => {
        dispatch(getUser());
        dispatch(getActiveCompany());
        getAuthorization();
        if (!loading) {
            //change this to if user does not have access to this application
            //redirect to the home page
            if (authUser) {

                history.push(initURL);

            } else {
                if (!authUser && error) {

                    window.location.href = LOGIN_PAGE;
                }
            }
        }
    }, [token, initURL]);

    const currentAppLocale = AppLocale[locale.locale];

    return (
        <ConfigProvider locale={currentAppLocale.antd}>
            <IntlProvider
                locale={currentAppLocale.locale}
                messages={currentAppLocale.messages}>

                <Switch>

                    <RestrictedRoute path={`${match.url}`} token={token} location={location}
                                     component={MainApp}/>
                </Switch>
            </IntlProvider>
        </ConfigProvider>
    )
};

export default memo(App);
